import React, { useState, useMemo } from "react";
import { View, TextInput, ScrollView, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import * as counties from "@/constants/fips/counties";
import { fipsToState, stateToFips } from "@/constants/fips/state";
import { cbsaToMetro, cbsaByState } from "@/constants/fips/cbsa";

// Types
type FipsCounty = {
  fips_code: string;
  name: string;
  state_code: string;
  state_name: string;
};

type CbsaMetro = {
  cbsa_code: string;
  name: string;
  primary_state: string;
  all_states: string[];
};

type ViewMode = "counties" | "metros";

export type FipsPickerProps = {
  selectedFipsCodes?: string[];
  onPick: (fipsCodes: string[]) => void;
  onCancel?: () => void;
};

// Utility Functions
function buildFipsDataset(): FipsCounty[] {
  const items: FipsCounty[] = [];

  // Iterate through all state county files
  Object.entries(counties).forEach(([, countyMap]) => {
    Object.entries(countyMap).forEach(([fips, name]) => {
      const stateCode = fips.substring(0, 2);
      // Skip state-level entries (e.g., "48000" for Texas)
      if (fips !== `${stateCode}000` && fips.length > 2) {
        const stateName = fipsToState[stateCode as keyof typeof fipsToState];
        if (stateName) {
          items.push({
            fips_code: fips,
            name: name as string,
            state_code: stateCode,
            state_name: stateName,
          });
        }
      }
    });
  });

  return items.sort((a, b) => {
    // Sort by state name first, then county name
    if (a.state_name !== b.state_name) {
      return a.state_name.localeCompare(b.state_name);
    }
    return a.name.localeCompare(b.name);
  });
}

// Build CBSA dataset
function buildCbsaDataset(): CbsaMetro[] {
  const items: CbsaMetro[] = [];

  for (const [code, name] of Object.entries(cbsaToMetro)) {
    // Parse state abbreviations from the metro name (after the comma)
    const stateMatch = (name as string).match(/,\s*([A-Z-]+)$/);
    if (stateMatch) {
      const statesStr = stateMatch[1];
      const allStates = statesStr.split("-");
      items.push({
        cbsa_code: code,
        name: name as string,
        primary_state: allStates[0],
        all_states: allStates,
      });
    }
  }

  return items.sort((a, b) => a.name.localeCompare(b.name));
}

// Get state name from abbreviation
function getStateNameFromAbbrev(abbrev: string): string {
  const entry = Object.entries(stateToFips).find(([name]) => {
    // Match state name to abbreviation
    const words = name.split(" ");
    if (words.length === 1) {
      return name.substring(0, 2).toUpperCase() === abbrev;
    }
    // Multi-word state names (e.g., "New York" -> "NY")
    return (
      words
        .map((w) => w[0])
        .join("")
        .toUpperCase() === abbrev
    );
  });
  return entry ? entry[0] : abbrev;
}

function getFipsName(code: string, allCounties: FipsCounty[]): string {
  // Check if it's a CBSA code (5 digits, but doesn't match county pattern)
  if (cbsaToMetro[code]) {
    return cbsaToMetro[code];
  }
  // Check if it's a state code (2 digits)
  if (code.length === 2) {
    return fipsToState[code as keyof typeof fipsToState] || code;
  }
  // County code
  const county = allCounties.find((c) => c.fips_code === code);
  return county ? `${county.name}, ${county.state_name}` : code;
}

// Check if code is a CBSA code
function isCbsaCode(code: string): boolean {
  return !!cbsaToMetro[code];
}

// Helper: Check if code is a state-level FIPS
function isStateFips(code: string): boolean {
  return code.length === 2;
}

// Helper: Get state code from any FIPS
function getStateCode(code: string): string {
  return code.substring(0, 2);
}

// Main Component
export function FipsPicker({ selectedFipsCodes = [], onPick, onCancel }: FipsPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<string[]>(selectedFipsCodes);
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>("metros");
  const [selectedStateFilter, setSelectedStateFilter] = useState<string | null>(null);

  // Build complete datasets
  const allCounties = useMemo(() => buildFipsDataset(), []);
  const allMetros = useMemo(() => buildCbsaDataset(), []);

  // Group counties by state
  const countiesByState = useMemo(() => {
    const grouped: Record<string, FipsCounty[]> = {};
    allCounties.forEach((county) => {
      if (!grouped[county.state_code]) {
        grouped[county.state_code] = [];
      }
      grouped[county.state_code].push(county);
    });
    return grouped;
  }, [allCounties]);

  // Get list of states in order
  const states = useMemo(() => {
    return Object.entries(fipsToState)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Get unique state abbreviations from metros for state filter dropdown
  const metroStates = useMemo(() => {
    const stateSet = new Set<string>();
    allMetros.forEach((metro) => {
      metro.all_states.forEach((s) => stateSet.add(s));
    });
    return Array.from(stateSet).sort();
  }, [allMetros]);

  // Filter metros based on search and state filter
  const filteredMetros = useMemo(() => {
    let filtered = allMetros;

    // Apply state filter
    if (selectedStateFilter) {
      filtered = filtered.filter((metro) => metro.all_states.includes(selectedStateFilter));
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (metro) => metro.name.toLowerCase().includes(searchLower) || metro.cbsa_code.includes(search),
      );
    }

    return filtered;
  }, [allMetros, search, selectedStateFilter]);

  // Filter counties based on search
  const filteredStates = useMemo(() => {
    if (!search) {
      return states;
    }

    const searchLower = search.toLowerCase();
    const matchingStates = new Set<string>();

    // Find states and counties that match search
    allCounties.forEach((county) => {
      if (
        county.name.toLowerCase().includes(searchLower) ||
        county.state_name.toLowerCase().includes(searchLower) ||
        county.fips_code.includes(searchLower)
      ) {
        matchingStates.add(county.state_code);
      }
    });

    return states.filter((state) => matchingStates.has(state.code));
  }, [search, states, allCounties]);

  // Auto-expand states when searching
  const effectiveExpandedStates = useMemo(() => {
    if (search) {
      // When searching, expand all matching states
      return new Set(filteredStates.map((s) => s.code));
    }
    return expandedStates;
  }, [search, filteredStates, expandedStates]);

  // Filter counties within each state based on search
  const getFilteredCountiesForState = (stateCode: string): FipsCounty[] => {
    const stateCounties = countiesByState[stateCode] || [];
    if (!search) {
      return stateCounties;
    }

    const searchLower = search.toLowerCase();
    return stateCounties.filter(
      (county) => county.name.toLowerCase().includes(searchLower) || county.fips_code.includes(searchLower),
    );
  };

  // Handlers
  const toggleStateExpansion = (stateCode: string) => {
    setExpandedStates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stateCode)) {
        newSet.delete(stateCode);
      } else {
        newSet.add(stateCode);
      }
      return newSet;
    });
  };

  const toggleCounty = (fipsCode: string) => {
    setSelectedCodes((prev) =>
      prev.includes(fipsCode) ? prev.filter((code) => code !== fipsCode) : [...prev, fipsCode],
    );
  };

  const removeCounty = (fipsCode: string) => {
    setSelectedCodes((prev) => prev.filter((code) => code !== fipsCode));
  };

  const handleStateSelectAll = (stateCode: string) => {
    const stateCounties = getFilteredCountiesForState(stateCode);
    const stateFipsCodes = stateCounties.map((c) => c.fips_code);
    const stateIsSelected = selectedCodes.includes(stateCode);

    if (stateIsSelected) {
      // Deselect the state (2-digit code)
      setSelectedCodes((prev) => prev.filter((code) => code !== stateCode));
    } else {
      // Select the state (2-digit code) and remove any individual counties
      setSelectedCodes((prev) => [...prev.filter((code) => !stateFipsCodes.includes(code)), stateCode]);
    }
  };

  const toggleMetro = (cbsaCode: string) => {
    setSelectedCodes((prev) =>
      prev.includes(cbsaCode) ? prev.filter((code) => code !== cbsaCode) : [...prev, cbsaCode],
    );
  };

  const handleConfirm = () => {
    onPick(selectedCodes);
  };

  const styles = StyleSheet.create({
    container: {
      padding: 8,
      gap: 8,
    },
    tabRow: {
      flexDirection: "row",
      gap: 4,
      marginBottom: 4,
    },
    tab: {
      flex: 1,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
    },
    tabActive: {
      backgroundColor: colors.primary,
    },
    tabText: {
      fontSize: 13,
      fontWeight: "500",
      color: colors.text,
    },
    tabTextActive: {
      color: "white",
    },
    filterRow: {
      flexDirection: "row",
      gap: 8,
    },
    searchInput: {
      flex: 1,
      height: 36,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      fontSize: 14,
      color: colors.text,
      backgroundColor: colors.background,
    },
    stateFilterButton: {
      height: 36,
      paddingHorizontal: 12,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      backgroundColor: colors.background,
    },
    stateFilterButtonActive: {
      borderColor: colors.primary,
      backgroundColor: colors.backgroundHover,
    },
    stateFilterText: {
      fontSize: 13,
      color: colors.text,
    },
    selectedSection: {
      gap: 8,
      paddingVertical: 4,
    },
    chipContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingVertical: 4,
      paddingLeft: 10,
      paddingRight: 6,
      backgroundColor: colors.primary,
      borderRadius: 16,
    },
    chipText: {
      fontSize: 12,
      color: "white",
    },
    chipRemove: {
      padding: 2,
    },
    scrollView: {
      maxHeight: 350,
    },
    stateHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: 10,
      paddingHorizontal: 8,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 6,
      marginVertical: 2,
    },
    stateHeaderContent: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flex: 1,
    },
    stateTitle: {
      fontSize: 15,
      fontWeight: "600",
    },
    countyCount: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    countyItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 8,
      paddingHorizontal: 8,
      paddingLeft: 28,
      borderRadius: 4,
    },
    countyItemSelected: {
      backgroundColor: colors.backgroundHover,
    },
    countyName: {
      fontSize: 14,
      flex: 1,
    },
    countyFips: {
      fontSize: 11,
      color: colors.textSecondary,
      fontFamily: "monospace",
    },
    metroItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    metroItemSelected: {
      backgroundColor: colors.backgroundHover,
    },
    metroInfo: {
      flex: 1,
    },
    metroName: {
      fontSize: 14,
      fontWeight: "500",
    },
    metroCode: {
      fontSize: 11,
      color: colors.textSecondary,
      fontFamily: "monospace",
      marginTop: 2,
    },
    stateFilterDropdown: {
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: 4,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      maxHeight: 200,
      minWidth: 150,
      zIndex: 100,
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    },
    stateFilterOption: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    stateFilterOptionActive: {
      backgroundColor: colors.backgroundHover,
    },
    stateFilterOptionText: {
      fontSize: 13,
    },
    emptyState: {
      padding: 20,
      alignItems: "center",
    },
    emptyText: {
      color: colors.textSecondary,
      textAlign: "center",
    },
    footer: {
      marginTop: 8,
    },
    selectedCount: {
      opacity: 0.7,
      marginBottom: 8,
    },
    footerButtons: {
      flexDirection: "row",
      gap: 8,
      justifyContent: "flex-end",
    },
    cancelButton: {
      height: 32,
      paddingHorizontal: 16,
      backgroundColor: "transparent",
      borderWidth: 1,
    },
    confirmButton: {
      height: 32,
      paddingHorizontal: 16,
    },
  });

  const [showStateDropdown, setShowStateDropdown] = useState(false);

  return (
    <View style={styles.container}>
      {/* Title */}
      <ThemedText type="subtitle">Select Markets</ThemedText>

      {/* View Mode Tabs */}
      <View style={styles.tabRow}>
        <Pressable
          style={[styles.tab, viewMode === "metros" && styles.tabActive]}
          onPress={() => {
            setViewMode("metros");
            setSearch("");
          }}
        >
          <ThemedText style={[styles.tabText, viewMode === "metros" && styles.tabTextActive]}>Metro Areas</ThemedText>
        </Pressable>
        <Pressable
          style={[styles.tab, viewMode === "counties" && styles.tabActive]}
          onPress={() => {
            setViewMode("counties");
            setSearch("");
            setSelectedStateFilter(null);
          }}
        >
          <ThemedText style={[styles.tabText, viewMode === "counties" && styles.tabTextActive]}>Counties</ThemedText>
        </Pressable>
      </View>

      {/* Search and Filter Row */}
      <View style={styles.filterRow}>
        <TextInput
          style={styles.searchInput}
          placeholder={
            viewMode === "metros" ? "Search metro areas or CBSA codes..." : "Search counties, states, or FIPS codes..."
          }
          placeholderTextColor={colors.textSecondary}
          value={search}
          onChangeText={setSearch}
          autoFocus
          testID="market-picker-search"
        />

        {/* State Filter (Metro mode only) */}
        {viewMode === "metros" && (
          <View style={{ position: "relative" }}>
            <Pressable
              style={[styles.stateFilterButton, selectedStateFilter && styles.stateFilterButtonActive]}
              onPress={() => setShowStateDropdown(!showStateDropdown)}
            >
              <ThemedText style={styles.stateFilterText}>{selectedStateFilter || "All States"}</ThemedText>
              <MaterialCommunityIcons
                name={showStateDropdown ? "chevron-up" : "chevron-down"}
                size={16}
                color={colors.text}
              />
            </Pressable>

            {/* State Dropdown */}
            {showStateDropdown && (
              <ScrollView style={styles.stateFilterDropdown}>
                <Pressable
                  style={[styles.stateFilterOption, !selectedStateFilter && styles.stateFilterOptionActive]}
                  onPress={() => {
                    setSelectedStateFilter(null);
                    setShowStateDropdown(false);
                  }}
                >
                  <ThemedText style={styles.stateFilterOptionText}>All States</ThemedText>
                </Pressable>
                {metroStates.map((state) => (
                  <Pressable
                    key={state}
                    style={[styles.stateFilterOption, selectedStateFilter === state && styles.stateFilterOptionActive]}
                    onPress={() => {
                      setSelectedStateFilter(state);
                      setShowStateDropdown(false);
                    }}
                  >
                    <ThemedText style={styles.stateFilterOptionText}>{state}</ThemedText>
                  </Pressable>
                ))}
              </ScrollView>
            )}
          </View>
        )}
      </View>

      {/* Selected Chips */}
      {selectedCodes.length > 0 && (
        <View style={styles.selectedSection}>
          <ThemedText type="defaultSemiBold">Selected ({selectedCodes.length})</ThemedText>
          <View style={styles.chipContainer}>
            {selectedCodes
              .filter((code) => {
                // Show state chips always
                if (isStateFips(code)) return true;
                // Only show county chips if their parent state is not selected
                const stateCode = getStateCode(code);
                return !selectedCodes.includes(stateCode);
              })
              .map((code) => (
                <Pressable key={code} style={styles.chip} onPress={() => removeCounty(code)} testID={`chip-${code}`}>
                  <ThemedText style={styles.chipText} numberOfLines={1}>
                    {getFipsName(code, allCounties)}
                  </ThemedText>
                  <View style={styles.chipRemove}>
                    <MaterialCommunityIcons name="close" size={14} color="white" />
                  </View>
                </Pressable>
              ))}
          </View>
        </View>
      )}

      {/* Scrollable List */}
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true}>
        {viewMode === "metros" ? (
          /* Metro Areas List */
          filteredMetros.length === 0 ? (
            <View style={styles.emptyState}>
              <ThemedText style={styles.emptyText}>
                No metro areas found{search ? ` matching &quot;${search}&quot;` : ""}
                {selectedStateFilter ? ` in ${selectedStateFilter}` : ""}
              </ThemedText>
            </View>
          ) : (
            filteredMetros.map((metro) => {
              const isSelected = selectedCodes.includes(metro.cbsa_code);

              return (
                <Pressable
                  key={metro.cbsa_code}
                  style={[styles.metroItem, isSelected && styles.metroItemSelected]}
                  onPress={() => toggleMetro(metro.cbsa_code)}
                  testID={`metro-${metro.cbsa_code}`}
                >
                  <MaterialCommunityIcons
                    name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={24}
                    color={isSelected ? colors.primary : colors.text}
                  />
                  <View style={styles.metroInfo}>
                    <ThemedText style={styles.metroName}>{metro.name}</ThemedText>
                    <ThemedText style={styles.metroCode}>CBSA: {metro.cbsa_code}</ThemedText>
                  </View>
                </Pressable>
              );
            })
          )
        ) : /* Counties List */
        filteredStates.length === 0 ? (
          <View style={styles.emptyState}>
            <ThemedText style={styles.emptyText}>No counties found matching &quot;{search}&quot;</ThemedText>
          </View>
        ) : (
          filteredStates.map((state) => {
            const stateCounties = getFilteredCountiesForState(state.code);
            const isExpanded = effectiveExpandedStates.has(state.code);
            const stateIsSelected = selectedCodes.includes(state.code);
            const selectedInState = stateIsSelected
              ? stateCounties.length
              : stateCounties.filter((c) => selectedCodes.includes(c.fips_code)).length;

            if (stateCounties.length === 0) {
              return null; // Don't show states with no matching counties
            }

            return (
              <View key={state.code}>
                {/* State Header */}
                <Pressable
                  style={styles.stateHeader}
                  onPress={() => toggleStateExpansion(state.code)}
                  testID={`state-header-${state.code}`}
                >
                  <View style={styles.stateHeaderContent}>
                    <MaterialCommunityIcons
                      name={isExpanded ? "chevron-down" : "chevron-right"}
                      size={20}
                      color={colors.text}
                    />
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        handleStateSelectAll(state.code);
                      }}
                      style={{ marginRight: 8 }}
                      testID={`state-select-all-${state.code}`}
                    >
                      <MaterialCommunityIcons
                        name={
                          stateIsSelected
                            ? "checkbox-marked"
                            : selectedInState > 0
                              ? "minus-box"
                              : "checkbox-blank-outline"
                        }
                        size={24}
                        color={stateIsSelected || selectedInState > 0 ? colors.primary : colors.text}
                      />
                    </Pressable>
                    <ThemedText style={styles.stateTitle}>{state.name}</ThemedText>
                    <ThemedText style={styles.countyCount}>
                      ({stateCounties.length} counties
                      {selectedInState > 0 ? `, ${selectedInState} selected` : ""})
                    </ThemedText>
                  </View>
                </Pressable>

                {/* County List (when expanded and state not selected) */}
                {isExpanded &&
                  !stateIsSelected &&
                  stateCounties.map((county) => {
                    const isSelected = selectedCodes.includes(county.fips_code);

                    return (
                      <Pressable
                        key={county.fips_code}
                        style={[styles.countyItem, isSelected && styles.countyItemSelected]}
                        onPress={() => toggleCounty(county.fips_code)}
                        testID={`county-${county.fips_code}`}
                      >
                        <MaterialCommunityIcons
                          name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                          size={24}
                          color={isSelected ? colors.primary : colors.text}
                        />
                        <ThemedText style={styles.countyName}>{county.name}</ThemedText>
                        <ThemedText style={styles.countyFips}>{county.fips_code}</ThemedText>
                      </Pressable>
                    );
                  })}
              </View>
            );
          })
        )}
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <ThemedText style={styles.selectedCount}>{selectedCodes.length} selected</ThemedText>
        <View style={styles.footerButtons}>
          {onCancel && (
            <Button
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          )}
          <Button
            text="Confirm"
            style={styles.confirmButton}
            onPress={handleConfirm}
            enabled={selectedCodes.length > 0}
            testID="confirm-button"
          />
        </View>
      </View>
    </View>
  );
}
